import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Autocomplete, Box, Chip, MenuItem, Stack, TextField } from '@mui/material';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../../components/CustomTableWithPagination/GlobalTable';
import { getCroStudents, getInactiveCroList } from '../../../../redux/features/crmSlice';
import { inactiveCroStudentsHeaderFn, STUDENT_STATUS_OPTIONS } from './utils/inactiveCroStudentsTableHead';
import ActivateStudentDialog from './components/ActivateStudentDialog';
import AssignCroDialog from './components/AssignCroDialog';

export default function InactiveCros() {
  const dispatch = useDispatch();

  /***********************************************/
  // redux state
  const inactiveCroList = useSelector((state) => state.crm.inactiveCroList.data);
  const croListLoading = useSelector((state) => state.crm.inactiveCroList.loading);

  const croStudents = useSelector((state) => state.crm.croStudents.data);
  const loading = useSelector((state) => state.crm.croStudents.loading);
  const meta = useSelector((state) => state.crm.croStudents.meta);

  /***********************************************/
  // react state
  const [cro, setCro] = useState(null);
  const [status, setStatus] = useState('all');
  //BUMPED AFTER EVERY SUCCESSFUL ROW ACTION SO THE TABLE REFETCHES ITSELF
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalState, setModalState] = useState({ actionKey: '', row: null });

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: 'asc' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  /***********************************************/
  // dispatch params
  const dispatchParams = useMemo(
    () => ({
      croId: cro?.userID,
      page: tableState?.page,
      limit: tableState?.pageSize,
      search: tableState?.search,
      status,
      sortBy: tableState?.sort?.field,
      sortOrder: tableState?.sort?.direction,
    }),
    [cro, status, tableState?.page, tableState?.pageSize, tableState?.search, tableState?.sort],
  );

  const inactiveCroStudentsHeader = useMemo(() => inactiveCroStudentsHeaderFn(), []);

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (!actionKey || !row) return;
    setModalState({ actionKey, row });
  }, []);

  const closeModal = useCallback(() => setModalState({ actionKey: '', row: null }), []);

  const handleActionSuccess = useCallback(() => {
    closeModal();
    setRefreshKey((prev) => prev + 1);
  }, [closeModal]);

  /***********************************************/
  // fetching

  //INACTIVE CROS UNDER THE LOGGED IN CRM
  useEffect(() => {
    const promise = dispatch(getInactiveCroList());
    return () => promise?.abort?.();
  }, [dispatch]);

  //STUDENTS OF THE SELECTED CRO . NOTHING IS FETCHED UNTIL A CRO IS PICKED
  useEffect(() => {
    if (!cro?.userID) return;
    const promise = dispatch(getCroStudents(dispatchParams));
    return () => promise?.abort?.();
  }, [dispatch, cro, dispatchParams, refreshKey]);

  //A NEW CRO OR STATUS MUST RESTART THE PAGINATION , OTHERWISE PAGE 4 OF THE PREVIOUS CRO IS REQUESTED
  const resetPage = () => setTableState((prev) => ({ ...prev, page: 1, checkedRowIds: [] }));

  /***********************************************/

  return (
    <GlobalWrapper title={'Inactive CROs'}>
      <ActivateStudentDialog
        open={modalState.actionKey === 'activate student'}
        setOpen={(val) => {
          if (!val) closeModal();
        }}
        row={modalState.row}
        onSuccess={handleActionSuccess}
      />

      <AssignCroDialog
        open={modalState.actionKey === 'assign cro'}
        setOpen={(val) => {
          if (!val) closeModal();
        }}
        row={modalState.row}
        onSuccess={handleActionSuccess}
      />

      <Stack gap={1}>
        <Box display={'flex'} gap={1} flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}>
          <Autocomplete
            options={inactiveCroList}
            loading={croListLoading}
            value={cro}
            getOptionLabel={(option) => option?.finalName || option?.name || ''}
            isOptionEqualToValue={(opt, val) => opt.userID === val.userID}
            onChange={(e, value) => {
              setCro(value);
              resetPage();
            }}
            sx={{ width: { xs: '100%', sm: 420 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={croListLoading ? 'Loading CROs...' : 'Inactive CRO'}
                placeholder="Choose an inactive CRO"
              />
            )}
          />

          <TextField
            select
            label="Student Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              resetPage();
            }}
            sx={{ width: { xs: '100%', sm: 220 } }}
          >
            {STUDENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {cro && (
            <Chip
              color="primary"
              variant="outlined"
              label={`${meta?.totalRecords ?? 0} student(s) under ${cro?.name}`}
            />
          )}
        </Box>

        {!cro && <Alert severity="info">Choose an inactive CRO to see the students still mapped to them.</Alert>}

        <GlobalTable
          rowUniqueId="course_enrollment_id"
          headers={inactiveCroStudentsHeader}
          rows={cro ? croStudents : []}
          tableState={tableState}
          setTableState={setTableState}
          loading={Boolean(cro) && loading}
          meta={cro ? meta : {}}
          onButtonActionClick={handleButtonActionClick}
          handleRefresh={cro ? () => setRefreshKey((prev) => prev + 1) : null}
        />
      </Stack>
    </GlobalWrapper>
  );
}
